import React, { useState, useMemo } from 'react'
import RoleCapabilityBadge from '../../../../components/RoleCapabilityBadge'
import {
  ACTION_CATEGORIES,
  evaluateRoleCapability,
  filterTreeByActionCategory,
  WRITE_ACTION_TYPES,
  SUBMODULE_DEPENDENCIES,
} from '../utils/roleCapabilities'

// ── Canonical 8-action theme map ─────────────────────────────────────────────
// Only the 8 standard actions; any unknown falls back to `default`
const ACTION_THEMES = {
  view: {
    icon: 'visibility',
    chip: 'border-on-surface-variant/20 bg-surface-container text-on-surface-variant',
    chipActive: 'bg-on-surface/90 border-on-surface/90 text-surface',
  },
  create: {
    icon: 'add_circle',
    chip: 'border-emerald-500/30 bg-emerald-500/8 text-emerald-700 dark:text-emerald-400',
    chipActive: 'bg-emerald-600 border-emerald-600 text-white',
  },
  edit: {
    icon: 'edit',
    chip: 'border-blue-500/30 bg-blue-500/8 text-blue-700 dark:text-blue-400',
    chipActive: 'bg-blue-600 border-blue-600 text-white',
  },
  delete: {
    icon: 'delete',
    chip: 'border-rose-500/30 bg-rose-500/8 text-rose-700 dark:text-rose-400',
    chipActive: 'bg-rose-600 border-rose-600 text-white',
  },
  archive: {
    icon: 'archive',
    chip: 'border-slate-500/30 bg-slate-500/8 text-slate-600 dark:text-slate-400',
    chipActive: 'bg-slate-700 border-slate-700 text-white',
  },
  approve: {
    icon: 'verified',
    chip: 'border-purple-500/30 bg-purple-500/8 text-purple-700 dark:text-purple-400',
    chipActive: 'bg-purple-600 border-purple-600 text-white',
  },
  export: {
    icon: 'download',
    chip: 'border-amber-500/30 bg-amber-500/8 text-amber-700 dark:text-amber-400',
    chipActive: 'bg-amber-600 border-amber-600 text-white',
  },
  import: {
    icon: 'upload',
    chip: 'border-indigo-500/30 bg-indigo-500/8 text-indigo-700 dark:text-indigo-400',
    chipActive: 'bg-indigo-600 border-indigo-600 text-white',
  },
  default: {
    icon: 'tune',
    chip: 'border-outline-variant/30 bg-surface-container text-on-surface-variant',
    chipActive: 'bg-primary border-primary text-on-primary',
  },
}

function getTheme(actionType) {
  return ACTION_THEMES[String(actionType || '').toLowerCase()] || ACTION_THEMES.default
}

// ── Single action permission chip ────────────────────────────────────────────
function ActionChip({ action, isSelected, disabled, onToggle }) {
  const theme = getTheme(action.type)
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onToggle(action.id, !isSelected)}
      title={action.full_label || action.label}
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-semibold transition-all select-none ${
        disabled
          ? 'cursor-not-allowed opacity-35 grayscale'
          : isSelected
          ? `${theme.chipActive} shadow-sm`
          : `${theme.chip} hover:opacity-80`
      }`}
    >
      <span className="material-symbols-outlined text-[12px]">
        {isSelected ? 'check' : theme.icon}
      </span>
      {action.label}
    </button>
  )
}

// ── Indeterminate checkbox for module/submodule header ────────────────────────
function SelectCheckbox({ checked, indeterminate, onClick, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded border transition-colors ${
        checked
          ? 'border-primary bg-primary text-on-primary'
          : indeterminate
          ? 'border-primary bg-primary/15 text-primary'
          : 'border-outline-variant/50 bg-surface-container-lowest hover:border-primary/50'
      }`}
    >
      {checked ? (
        <span className="material-symbols-outlined text-[12px] font-bold">check</span>
      ) : indeterminate ? (
        <span className="block h-[3px] w-[9px] rounded-full bg-primary" />
      ) : null}
    </button>
  )
}

// ── Filter tab button ─────────────────────────────────────────────────────────
function FilterTab({ active, onClick, icon, label, count, activeColor = 'bg-primary text-on-primary' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold transition-all ${
        active
          ? `${activeColor} shadow-sm`
          : 'text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface'
      }`}
    >
      {icon && <span className="material-symbols-outlined text-[12px]">{icon}</span>}
      {label}
      <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-black ${active ? 'bg-white/20' : 'bg-surface-container-high'}`}>
        {count}
      </span>
    </button>
  )
}

// ── Preset / utility button ──────────────────────────────────────────────────
function ToolButton({ onClick, icon, label, variant = 'neutral', title }) {
  const variants = {
    neutral: 'border-outline-variant/30 bg-surface-container-lowest text-on-surface hover:bg-surface-container-low',
    emerald: 'border-emerald-500/30 bg-emerald-500/8 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/15',
    purple: 'border-purple-500/30 bg-purple-500/8 text-purple-700 dark:text-purple-400 hover:bg-purple-500/15',
    rose: 'border-rose-500/30 bg-rose-500/8 text-rose-700 dark:text-rose-400 hover:bg-rose-500/15',
    primary: 'border-primary/30 bg-primary/8 text-primary hover:bg-primary/15',
  }
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-bold transition-all ${variants[variant] || variants.neutral}`}
    >
      {icon && <span className="material-symbols-outlined text-[12px]">{icon}</span>}
      {label}
    </button>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function PrivilegesMatrixTree({
  tree = [],
  selectedIds = [],
  onToggleAction,
  onToggleSubmodule,
  onToggleModule,
  onSelectAll,
  onClearAll,
  onSelectDefaults,
  onSelectViewOnly,
  onSelectViewApprove,
  onStripWrites,
  roleCapability,
}) {
  // Defensive deduplication and hierarchical grouping of submodules
  const cleanTree = useMemo(() => {
    return (tree || []).map((mod) => {
      const processedSubs = (mod.submodules || []).map((sub) => {
        const uniqueActions = (sub.actions || []).reduce((acc, act) => {
          const actType = String(act.type || '').toLowerCase()
          const existingIdx = acc.findIndex((a) => String(a.type || '').toLowerCase() === actType)
          if (existingIdx === -1) {
            acc.push(act)
          } else if (act.name && act.name.endsWith('Add') && !acc[existingIdx].name?.endsWith('Add')) {
            // Prefer canonical ...Add action over redundant ...Create action
            acc[existingIdx] = act
          }
          return acc
        }, [])

        const depMeta = SUBMODULE_DEPENDENCIES[sub.key]
        const parentKey = sub.parent_submodule_key || depMeta?.parentKey || null

        return {
          ...sub,
          parent_submodule_key: parentKey,
          actions: uniqueActions,
        }
      })

      // Ensure dependent submodules are placed directly beneath their parent submodule
      const parentSubs = []
      const childSubsByParent = {}

      processedSubs.forEach((sub) => {
        if (sub.parent_submodule_key) {
          if (!childSubsByParent[sub.parent_submodule_key]) {
            childSubsByParent[sub.parent_submodule_key] = []
          }
          childSubsByParent[sub.parent_submodule_key].push(sub)
        } else {
          parentSubs.push(sub)
        }
      })

      const orderedSubs = []
      parentSubs.forEach((parent) => {
        orderedSubs.push(parent)
        if (childSubsByParent[parent.key]) {
          orderedSubs.push(...childSubsByParent[parent.key])
          delete childSubsByParent[parent.key]
        }
      })
      Object.values(childSubsByParent).forEach((orphans) => {
        orderedSubs.push(...orphans)
      })

      return {
        ...mod,
        submodules: orderedSubs,
      }
    })
  }, [tree])

  const [search, setSearch] = useState('')
  const [filterCat, setFilterCat] = useState(ACTION_CATEGORIES.ALL)
  const [expanded, setExpanded] = useState(() => {
    const init = {}
    cleanTree.slice(0, 2).forEach((m) => { init[m.key] = true })
    return init
  })

  const selectedSet = useMemo(() => new Set(selectedIds.map(Number)), [selectedIds])

  const currentCapability = useMemo(
    () => roleCapability || evaluateRoleCapability(selectedIds, cleanTree),
    [roleCapability, selectedIds, cleanTree],
  )

  // Action counts for filter tabs
  const counts = useMemo(() => {
    let view = 0, write = 0, approve = 0, total = 0
    cleanTree.forEach((m) => m.submodules?.forEach((s) => s.actions?.forEach((a) => {
      total++
      const t = String(a.type || '').toLowerCase()
      if (t === 'view') view++
      else if (t === 'approve') approve++
      else write++
    })))
    return { total, view, write, approve }
  }, [cleanTree])

  const totalPrivilegeCount = useMemo(() => {
    let c = 0
    cleanTree.forEach((m) => m.submodules.forEach((s) => { c += s.actions.length }))
    return c
  }, [cleanTree])

  // Apply category filter then search
  const filteredTree = useMemo(() => {
    const catTree = filterTreeByActionCategory(cleanTree, filterCat)
    const q = search.trim().toLowerCase()
    if (!q) return catTree
    return catTree
      .map((mod) => {
        const subs = mod.submodules.filter((sub) => {
          const text = `${sub.label} ${sub.key} ${sub.actions.map((a) => `${a.label} ${a.type}`).join(' ')}`.toLowerCase()
          return text.includes(q) || mod.label.toLowerCase().includes(q)
        })
        if (subs.length === 0 && !mod.label.toLowerCase().includes(q)) return null
        return { ...mod, submodules: subs.length > 0 ? subs : mod.submodules }
      })
      .filter(Boolean)
  }, [cleanTree, filterCat, search])

  const toggleExpand = (key) => setExpanded((p) => ({ ...p, [key]: !p[key] }))
  const expandAll = () => {
    const all = {}
    cleanTree.forEach((m) => { all[m.key] = true })
    setExpanded(all)
  }
  const collapseAll = () => setExpanded({})
  const allExpanded = Object.keys(expanded).length > 0

  return (
    <div className="flex flex-col rounded-2xl border border-outline-variant/30 bg-surface-container-lowest overflow-hidden">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 border-b border-outline-variant/20 bg-surface-container-low/30 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
        {/* Title + badge + counter */}
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <span className="material-symbols-outlined text-xl">admin_panel_settings</span>
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-sm font-extrabold text-on-surface">Access Rights Matrix</h2>
              <span className="rounded-full bg-surface-container-high px-2 py-0.5 text-[10px] font-black text-on-surface-variant">
                {selectedIds.length} / {totalPrivilegeCount}
              </span>
              <RoleCapabilityBadge capability={currentCapability} />
            </div>
            <p className="text-[11px] text-on-surface-variant mt-0.5">
              Toggle granular action rights per module. View access must be enabled before granting other actions.
            </p>
          </div>
        </div>

        {/* Toolbar: search + presets + utilities */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative">
            <span className="material-symbols-outlined pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[13px] text-on-surface-variant">search</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search module or action..."
              className="w-44 rounded-lg border border-outline-variant/30 bg-surface-container-lowest py-1.5 pl-8 pr-7 text-[11px] text-on-surface outline-none placeholder:text-on-surface-variant/70 focus:border-primary/50 focus:w-56 transition-all"
            />
            {search && (
              <button type="button" onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-on-surface-variant hover:text-on-surface">
                ✕
              </button>
            )}
          </div>

          {/* Separator */}
          <span className="h-5 w-px bg-outline-variant/30" />

          {/* 1-click role presets */}
          <ToolButton onClick={onSelectViewOnly} icon="visibility" label="View Only" variant="emerald" title="Select all View rights only" />
          <ToolButton onClick={onSelectViewApprove} icon="verified" label="View + Approve" variant="purple" title="Select View and Approve rights" />
          <ToolButton onClick={onStripWrites} icon="shield" label="Strip Writes" variant="rose" title="Remove all write permissions" />

          {/* Separator */}
          <span className="h-5 w-px bg-outline-variant/30" />

          {/* Standard ops */}
          <ToolButton onClick={onSelectAll} label="All" variant="neutral" />
          <ToolButton onClick={onClearAll} label="Clear" variant="neutral" />
          <ToolButton onClick={onSelectDefaults} icon="restart_alt" label="Defaults" variant="primary" />

          {/* Expand / Collapse */}
          <button
            type="button"
            onClick={allExpanded ? collapseAll : expandAll}
            title={allExpanded ? 'Collapse all' : 'Expand all'}
            className="rounded-lg border border-outline-variant/30 bg-surface-container-lowest p-1.5 text-on-surface-variant hover:bg-surface-container-low transition-colors"
          >
            <span className="material-symbols-outlined text-[16px]">{allExpanded ? 'unfold_less' : 'unfold_more'}</span>
          </button>
        </div>
      </div>

      {/* ── Filter Tabs ─────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-1.5 border-b border-outline-variant/15 bg-surface-container-low/15 px-5 py-2">
        <FilterTab active={filterCat === ACTION_CATEGORIES.ALL} onClick={() => setFilterCat(ACTION_CATEGORIES.ALL)} label="All" count={counts.total} />
        <FilterTab active={filterCat === ACTION_CATEGORIES.VIEW} onClick={() => setFilterCat(ACTION_CATEGORIES.VIEW)} icon="visibility" label="View" count={counts.view} activeColor="bg-on-surface text-surface" />
        <FilterTab active={filterCat === ACTION_CATEGORIES.WRITE} onClick={() => setFilterCat(ACTION_CATEGORIES.WRITE)} icon="edit" label="Writes" count={counts.write} activeColor="bg-blue-600 text-white" />
        <FilterTab active={filterCat === ACTION_CATEGORIES.APPROVE} onClick={() => setFilterCat(ACTION_CATEGORIES.APPROVE)} icon="verified" label="Approvals" count={counts.approve} activeColor="bg-purple-600 text-white" />

        {filterCat !== ACTION_CATEGORIES.ALL && (
          <button type="button" onClick={() => setFilterCat(ACTION_CATEGORIES.ALL)}
            className="ml-auto text-[10px] font-semibold text-primary hover:underline">
            Clear filter
          </button>
        )}
      </div>

      {/* ── Module Tree ─────────────────────────────────────────────────────── */}
      {filteredTree.length === 0 ? (
        <div className="py-14 text-center">
          <span className="material-symbols-outlined text-3xl text-on-surface-variant/40">search_off</span>
          <p className="mt-2 text-xs text-on-surface-variant">No results for &quot;{search}&quot;</p>
        </div>
      ) : (
        <div className="divide-y divide-outline-variant/10">
          {filteredTree.map((mod) => {
            const isOpen = Boolean(expanded[mod.key] || search)
            const allIds = mod.submodules.flatMap((s) => s.actions.map((a) => a.id))
            const selectedCount = allIds.filter((id) => selectedSet.has(id)).length
            const allChecked = allIds.length > 0 && selectedCount === allIds.length
            const someChecked = selectedCount > 0 && !allChecked

            return (
              <div key={mod.key}>

                {/* ── Module row ─────────────────────────────────────────── */}
                <div
                  onClick={() => toggleExpand(mod.key)}
                  className="flex cursor-pointer items-center gap-3 bg-surface-container-low/20 px-5 py-3 hover:bg-surface-container-low/35 transition-colors select-none"
                >
                  <SelectCheckbox
                    checked={allChecked}
                    indeterminate={someChecked}
                    onClick={(e) => { e.stopPropagation(); onToggleModule(mod, !allChecked) }}
                    title="Toggle all in this module"
                  />
                  <span className="material-symbols-outlined text-base text-primary">{mod.icon || 'folder'}</span>
                  <span className="text-sm font-bold text-on-surface">{mod.label}</span>
                  <span className="text-[10px] font-semibold text-on-surface-variant/70">
                    {selectedCount}/{allIds.length}
                  </span>
                  <span className={`material-symbols-outlined ml-auto text-base text-on-surface-variant transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                    expand_more
                  </span>
                </div>

                {/* ── Submodule rows ─────────────────────────────────────── */}
                {isOpen && (
                  <div className="divide-y divide-outline-variant/8">
                    {mod.submodules.map((sub) => {
                      const depMeta = SUBMODULE_DEPENDENCIES[sub.key]
                      const parentKey = sub.parent_submodule_key || depMeta?.parentKey || null
                      const isChild = Boolean(parentKey)

                      const parentSub = isChild ? mod.submodules.find((s) => s.key === parentKey) : null
                      const parentViewAct = parentSub?.actions.find((a) => a.type === 'view')
                      const hasParentView = parentViewAct ? selectedSet.has(parentViewAct.id) : true

                      const hasChildren = mod.submodules.some(
                        (s) => (s.parent_submodule_key || SUBMODULE_DEPENDENCIES[s.key]?.parentKey) === sub.key
                      )

                      const viewAct = sub.actions.find((a) => a.type === 'view')
                      const restActs = sub.actions.filter((a) => a.type !== 'view')
                      const hasView = viewAct ? selectedSet.has(viewAct.id) : false
                      const subAllIds = sub.actions.map((a) => a.id)
                      const subSelCount = subAllIds.filter((id) => selectedSet.has(id)).length
                      const subAllChecked = subAllIds.length > 0 && subSelCount === subAllIds.length
                      const subSomeChecked = subSelCount > 0 && !subAllChecked

                      return (
                        <div
                          key={sub.key}
                          className={`flex flex-col gap-2 px-5 py-3 transition-colors sm:flex-row sm:items-center ${
                            isChild
                              ? 'pl-16 sm:pl-20 bg-surface-container-low/20 hover:bg-surface-container-low/30'
                              : 'pl-12 hover:bg-surface-container-low/10'
                          }`}
                        >
                          {/* Left: Submodule label */}
                          <div className="flex items-center gap-2.5 min-w-[240px]">
                            {isChild && (
                              <span
                                className="material-symbols-outlined text-[16px] text-on-surface-variant/40 shrink-0 select-none"
                                title={`Sub-feature of ${parentSub?.label || 'Project Financials'}`}
                              >
                                subdirectory_arrow_right
                              </span>
                            )}
                            <SelectCheckbox
                              checked={subAllChecked}
                              indeterminate={subSomeChecked}
                              onClick={() => onToggleSubmodule(sub, !subAllChecked)}
                              title={`Toggle all in ${sub.label}`}
                            />
                            <div>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <p className="text-xs font-semibold text-on-surface leading-tight">{sub.label}</p>
                                {hasChildren && (
                                  <span className="inline-flex items-center gap-1 rounded bg-surface-container-high px-1.5 py-0.5 text-[9px] font-semibold text-on-surface-variant">
                                    <span className="material-symbols-outlined text-[10px]">account_balance_wallet</span>
                                    Lead Financial Module
                                  </span>
                                )}
                                {isChild && !hasParentView && (
                                  <span
                                    className="inline-flex items-center gap-0.5 rounded bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-semibold text-amber-700 dark:text-amber-400"
                                    title={`Requires ${parentSub?.label || 'Project Financials'} View (will auto-enable upon selection)`}
                                  >
                                    <span className="material-symbols-outlined text-[10px]">lock</span>
                                    Requires Project Financials
                                  </span>
                                )}
                                {isChild && hasParentView && (
                                  <span
                                    className="inline-flex items-center gap-0.5 rounded bg-surface-container-high px-1.5 py-0.5 text-[9px] font-medium text-on-surface-variant/70"
                                    title={`Sub-feature of ${parentSub?.label || 'Project Financials'}`}
                                  >
                                    Sub-feature
                                  </span>
                                )}
                              </div>
                              <p className="text-[10px] text-on-surface-variant/60 font-mono mt-0.5">{sub.key}</p>
                            </div>
                          </div>

                          {/* Right: Action chips */}
                          <div className="flex flex-wrap items-center gap-1.5 sm:ml-auto">
                            {/* View chip always first */}
                            {viewAct && (
                              <ActionChip
                                action={viewAct}
                                isSelected={hasView}
                                disabled={false}
                                onToggle={(id, next) => onToggleAction(id, next, null)}
                              />
                            )}
                            {/* Rest of actions — disabled if view is off */}
                            {restActs.map((act) => (
                              <ActionChip
                                key={act.id}
                                action={act}
                                isSelected={selectedSet.has(act.id)}
                                disabled={!hasView}
                                onToggle={(id, next) => onToggleAction(id, next, viewAct?.id)}
                              />
                            ))}
                            {sub.actions.length === 0 && (
                              <span className="text-[10px] italic text-on-surface-variant/50">No actions defined</span>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
